import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { BellOff, BellRing } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { useCustomerAgentAvailable } from '@/features/whatsapp/hooks/useCustomerAgentAvailable';
import { useTrayAction, useTrayNotices, type TrayNotice } from '@/shared/components/NoticeTray';
import { pointsToCurrentRoute } from '@/shared/lib/current-route';
import { createAlertController, type AlertController } from '../lib/alert-controller';
import type { AlertChannelFactory } from '../lib/alert-leader';
import { ALERT_SOURCES, type AlertSource } from '../lib/alert-sources';
import { formatClock } from '../lib/wait-time';
import styles from './ConversationAlerts.module.css';

/* ==========================================================================
   Alertas globais de atendimento (change customer-agent-handoff, D19)

   Em qualquer tela do painel, para quem vê alguma fonte de alerta
   (`lib/alert-sources.ts`): faixa com uma linha por fonte, não dispensável,
   que some sozinha em 0; som na pendência nova e a cada 60 s só na aba
   líder; título "(N) "; notificação do navegador na líder oculta ou sem
   foco, sem nome, telefone ou texto. As decisões ficam no
   `alert-controller.ts`; aqui só a leitura das fontes e a faixa. Um só
   `ConversationAlerts` no painel (o do `PanelLayout`): fonte nova entra no
   registro, nunca num segundo componente. Tudo em `ph-no-capture`.

   Botões (relatório de UX, P1-12): um só, "Ativar som e avisos", enquanto
   falta liberar o som ou decidir a notificação — o mesmo clique faz as duas
   coisas, que aceitam o mesmo gesto —, e, com o som liberado, o ícone
   "Silenciar por 30 min", que vira "Som volta às HH:MM". O link de cada
   linha some quando leva à tela em que a pessoa já está ("Ver conversas"
   no Atendimento, "Ver pedidos" em Pedidos). Cada linha também vai para o
   resumo dos avisos do topo, urgente quando tem prioritário, e o "Ativar som
   e avisos", enquanto falta, fica também ao lado do resumo com a lista
   fechada (`useTrayAction`).
   ========================================================================== */

export interface ConversationAlertsProps {
  /** As fontes registradas; os testes passam as deles. */
  sources?: readonly AlertSource[];
  /** O canal entre as abas; o `BroadcastChannel` do navegador, por padrão (os testes passam um falso). */
  channelFactory?: AlertChannelFactory;
}

/**
 * A faixa, o som, o título e a notificação do atendimento. Sem nenhuma fonte
 * que o usuário veja (a Cozinha), não renderiza nada: nenhuma leitura,
 * nenhum canal, nenhum som.
 */
export function ConversationAlerts({ sources = ALERT_SOURCES, channelFactory }: ConversationAlertsProps) {
  const permissions = useAuthStore((state) => state.user?.permissions);
  const tenantId = useAuthStore((state) => state.selectedTenant?.id ?? '');
  const visible = useMemo(
    () => sources.filter((source) => permissions?.includes(source.permission) ?? false),
    [sources, permissions],
  );
  if (!tenantId || visible.length === 0) return null;
  return <ConversationAlertsContent key={tenantId} tenantId={tenantId} sources={visible} channelFactory={channelFactory} />;
}

interface ContentProps {
  tenantId: string;
  sources: readonly AlertSource[];
  channelFactory?: AlertChannelFactory;
}

function ConversationAlertsContent({ tenantId, sources, channelFactory }: ContentProps) {
  const navigate = useNavigate();
  const controller = useMemo(
    () => createAlertController({ tenantId, sources, channelFactory }),
    [tenantId, sources, channelFactory],
  );

  useEffect(() => {
    controller.start();
    return () => controller.stop();
  }, [controller]);

  useEffect(() => {
    controller.setOpenHandler((href) => navigate(href));
  }, [controller, navigate]);

  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
  const location = useLocation();

  useTrayNotices(
    snapshot.lines.map(({ sourceId, state }): TrayNotice => ({
      // A contagem na chave: o resumo é anunciado de novo quando chega ou sai alguém, e não a cada minuto de espera.
      key: `${sourceId}:${state.count}:${state.highPriorityCount}`,
      text: state.summary || state.line,
      level: state.highPriorityCount > 0 ? 'urgent' : 'normal',
    })),
  );

  const soundLocked = !snapshot.anyAudioUnlocked;
  const notificationsUndecided = snapshot.notificationPermission === 'default';
  const activate = () => {
    // O som primeiro: o pedido de permissão é assíncrono, e o gesto vale para os dois.
    if (soundLocked) controller.enableSound();
    if (notificationsUndecided) void controller.requestNotifications();
  };
  const needsActivation = snapshot.total > 0 && (soundLocked || notificationsUndecided);
  // Com a lista fechada, o mesmo botão fica ao lado do resumo do topo: a permissão de notificação
  // só sai de um clique nele, e quem nunca abre a lista ficaria sem o aviso fora da aba.
  useTrayAction(needsActivation ? activationLabel(soundLocked, notificationsUndecided) : null, activate);

  return (
    <>
      {sources.map((source) => (
        <SourceProbe key={source.id} source={source} controller={controller} />
      ))}
      {snapshot.total > 0 && (
        // Não dispensável: some sozinha quando não há mais pendência.
        <div className={`${styles.banner} ph-no-capture`} role="status" data-testid="conversation-alerts">
          <BellRing size={16} className={styles.icon} aria-hidden="true" />
          <ul className={styles.lines}>
            {snapshot.lines.map(({ sourceId, state }) => {
              const dismiss = sources.find((source) => source.id === sourceId)?.dismiss;
              return (
                <li key={sourceId} className={styles.line} data-alert-source={sourceId}>
                  <span className={styles.text}>{state.line}</span>
                  {!pointsToCurrentRoute(state.link.to, location) && (
                    <Link to={state.link.to} className={styles.link}>
                      {state.link.label}
                    </Link>
                  )}
                  {state.dismissLabel && dismiss && (
                    <button type="button" className={styles.action} onClick={dismiss}>
                      {state.dismissLabel}
                    </button>
                  )}
                  {state.note && <span className={styles.note}>{state.note}</span>}
                </li>
              );
            })}
          </ul>
          <div className={styles.actions}>
            {needsActivation && (
              <button type="button" className={styles.action} onClick={activate}>
                {activationLabel(soundLocked, notificationsUndecided)}
              </button>
            )}
            {!soundLocked &&
              (snapshot.mutedUntil !== null ? (
                <span className={styles.muted}>
                  Som volta às {formatClock(new Date(snapshot.mutedUntil).toISOString())}
                </span>
              ) : (
                <button
                  type="button"
                  className={styles.iconAction}
                  onClick={() => controller.mute()}
                  aria-label="Silenciar por 30 min"
                  title="Silenciar por 30 min"
                >
                  <BellOff size={16} aria-hidden="true" />
                </button>
              ))}
          </div>
        </div>
      )}
    </>
  );
}

/** O botão diz só o que ainda falta: o som, o aviso do navegador ou os dois. */
function activationLabel(soundLocked: boolean, notificationsUndecided: boolean): string {
  if (soundLocked && notificationsUndecided) return 'Ativar som e avisos';
  return soundLocked ? 'Ativar som' : 'Ativar avisos';
}

interface SourceProbeProps {
  source: AlertSource;
  controller: AlertController;
}

/** Só consulta a fonte com a área do Atendente IA disponível para a permissão dela. */
function SourceProbe({ source, controller }: SourceProbeProps) {
  const { available } = useCustomerAgentAvailable(source.permission);
  useEffect(() => {
    if (!available) controller.setSourceState(source.id, null);
  }, [available, controller, source.id]);
  return available ? <SourceReporter source={source} controller={controller} /> : null;
}

function SourceReporter({ source, controller }: SourceProbeProps) {
  const state = source.useAlertState();
  // O estado volta como objeto novo a cada render: entrega pelo conteúdo.
  const serialized = state ? JSON.stringify(state) : '';
  useEffect(() => {
    controller.setSourceState(source.id, serialized ? JSON.parse(serialized) : null);
  }, [serialized, controller, source.id]);
  useEffect(() => () => controller.setSourceState(source.id, null), [controller, source.id]);
  return null;
}
